import {
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  type TouchEvent as ReactTouchEvent,
  useRef,
} from 'react';
import { useLongPress } from 'use-long-press';
import More from '@/assets/images/icons/more.svg?react';
import MediaPreviewContent from '@/components/common/media-content/media-preview-content';
import type { Collection } from '@/hooks/collections/use-collections';
import useUserPlatform from '@/hooks/use-user-platform';
import { ConversationMessageContentType } from '@/models/conversations/conversation';
import cn from '@/utils/merge-class-names';

interface Props {
  collection: Collection;
  className?: string;
  onContextMenu?: (position: [number, number] | null) => void;
}

export default function CollectionItem({ collection, className, onContextMenu }: Props) {
  const itemRef = useRef<HTMLDivElement | null>(null);
  const { supportsTouch } = useUserPlatform();

  const isImage = collection.Type === ConversationMessageContentType.Image;
  const isVideo = collection.Type === ConversationMessageContentType.Video;

  const handleContextMenu = (e: MouseEvent | ReactMouseEvent | ReactTouchEvent | ReactPointerEvent) => {
    e.preventDefault();
    e.stopPropagation();

    let position: [number, number] = [0, 0];
    if ('clientX' in e) {
      position = [e.clientX, e.clientY];
    } else if ('touches' in e && e.touches.length > 0) {
      const touch = e.touches.item(0);
      position = [touch.clientX, touch.clientY];
    }

    onContextMenu?.(position);
  };

  const bindLongPress = useLongPress(handleContextMenu, { threshold: 800 });

  return (
    <div
      className={cn('aspect-square relative  size-34 desktop:size-40 rounded bg-metal-50 transition-colors', className)}
      ref={itemRef}
      {...(supportsTouch ? bindLongPress() : { onContextMenu: handleContextMenu })}>
      {!!onContextMenu && (
        <button
          type='button'
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleContextMenu(e);
          }}
          className={cn('absolute z-10 right-2 top-2 text-secondary', {
            'text-neutral-0': isImage || isVideo,
          })}>
          <More className='size-4' />
        </button>
      )}
      <MediaPreviewContent
        type={collection.Type}
        src={collection.Content}
      />
    </div>
  );
}
